/**
 * ToastContext
 * Provides global toast notifications across the entire application
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import ToastContainer from '../components/Toast/ToastContainer';
import { ToastType } from '../components/Toast/Toast';
import { ToastData } from '../components/Toast/ToastContainer';

interface ToastContextType {
  toasts: ToastData[];
  addToast: (message: string, type?: ToastType, duration?: number, title?: string) => void;
  removeToast: (id: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  notify: (title: string, message: string, type?: ToastType, href?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((
    message: string,
    type: ToastType = 'info',
    duration?: number,
    title?: string,
    href?: string,
    variant?: ToastData['variant']
  ) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts(prev => [
      ...prev,
      { id, type, message, title, duration, href, variant }
    ]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message: string, title?: string) => addToast(message, 'success', undefined, title), [addToast]);
  const error = useCallback((message: string, title?: string) => addToast(message, 'error', undefined, title), [addToast]);
  const warning = useCallback((message: string, title?: string) => addToast(message, 'warning', undefined, title), [addToast]);
  const info = useCallback((message: string, title?: string) => addToast(message, 'info', undefined, title), [addToast]);

  const notify = useCallback((
    title: string,
    message: string,
    type: ToastType = 'info',
    href?: string
  ) => {
    addToast(message, type, 6000, title, href, 'notification');
  }, [addToast]);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        addToast,
        removeToast,
        success,
        error,
        warning,
        info,
        notify
      }}
    >
      {children}
      {/* Global Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </ToastContext.Provider>
  );
};

